import { useState, createRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX } from 'react-icons/fi';
import { Formik, Form, Field } from 'formik';
import ReCAPTCHA from 'react-google-recaptcha';
import * as yup from 'yup';
import ApiClient from 'src/lib/api_client';
import useFlash from 'src/hooks/useFlash';
import useEnhanceApiClient from 'src/hooks/useEnhanceApiClient';
import { useTranslations } from 'next-intl';
import ErrorMesseageTranslation from 'src/components/ErrorMesseageTranslation';

const topics = [
  'teamBuildingActivities',
  'giftCards',
  'others',
];

const RECAPTCHA_KEY = 'g-recaptcha-response';

const ValidationSchema = yup.object().shape({
    name: yup
      .string()
      .required('validationNameRequired')
      .typeError('validationNameTypeError'),
    email: yup
      .string()
      .email('validationEmailFormatError')
      .required('validationEmailRequired')
      .typeError('validationEmailTypeError'),
    message: yup
      .string()
      .required('validationudgetRequirementsRequired')
      .typeError('validationudgetRequirementsTypeError'),
    topic: yup.string().required('validationInquiryTopicRequired').typeError('validationInquiryTopicTypeError'),
    [RECAPTCHA_KEY]: yup.string().required().typeError('validationCaptchaTypeError'),
  });

const CorporateInquiry: React.FC = () => {
  const translation = useTranslations('components.CorporateInquiry')
  const createCorporateInquiry = useEnhanceApiClient(ApiClient.createCorporateInquiry)
  const [, setFlash] = useFlash();
  const recaptchaRef = createRef<ReCAPTCHA>();
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn btn-primary"
        onClick={() => {
          setShowModal(true);
        }}
      >
        {translation('getInTouch')}
      </button>
      <Modal
        show={showModal}
        dialogClassName="modal-dialog-centered modal-full-width--sp modal-dialog-scrollable"
        onHide={() => {
          setShowModal(false);
        }}
      >
        <Modal.Header>
          <div className="tw-flex tw-items-start tw-justify-between tw-w-full">
            <div>
              <div className="tw-font-styleSans tw-font-semibold tw-text-lg md:tw-text-2xl tw-leading-none md:tw-leading-none tw-mb-2 tw-mr-8">
                {translation('getInTouch1')}
              </div>
              <div>{translation('needTeamRewards')}!</div>
            </div>
            <button type="button" className="close tw-flex-shrink-0" onClick={() => setShowModal(false)}>
              <FiX size={24} className="tw-text-brand-navy" />
            </button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={{
              name: '',
              email: '',
              quantity: undefined,
              topic: '',
              message: '',
            }}
            validationSchema={ValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
              createCorporateInquiry(values)
                .then((result) => {
                  const [json] = result
                  if ('error' in json && json.error) {
                    setError(json.error);
                  } else {
                    setShowModal(false);
                    setFlash({ notice: translation('inquirySubmittedSuccessfully') })
                  }
                })
                .catch((e) => {
                  if (e instanceof Error) {
                    setError(e.message);
                  }
                })
                .finally(() => setSubmitting(false));
            }}
          >
            {(formik) => (
              <Form>
                <div className="form-group">
                  <Field
                    name="name"
                    type="text"
                    className="form-control"
                    placeholder={translation('yourName')}
                  />
                  <ErrorMesseageTranslation name="name" />
                </div>
                <div className="form-group">
                  <Field
                    name="email"
                    type="text"
                    className="form-control"
                    placeholder={translation('workEmail')}
                  />
                  <ErrorMesseageTranslation name="email" />
                </div>
                <div className="form-group">
                  <Field
                    name="quantity"
                    type="number"
                    className="form-control"
                    placeholder={translation('NoOfEmployees')}
                  />
                </div>
                <div className="form-group">
                  <Field
                    component="select"
                    name="topic"
                    className="form-control custom-select"
                    defaultValue={''}
                  >
                    <option value={''} disabled hidden>
                      {translation('inquiryTopic')}
                    </option>
                    {topics.map((topic) => (
                      <option key={topic} value={translation(topic)}>
                        {translation(topic)}
                      </option>
                    ))}
                  </Field>
                  <ErrorMesseageTranslation name="topic" />
                </div>
                <div className="form-group">
                  <Field
                    component="textarea"
                    name="message"
                    placeholder={translation('whatIsYourBudget')}
                    className="form-control"
                    rows={3}
                  />
                  <ErrorMesseageTranslation name="message" />
                </div>
                <div className="form-group">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
                    onChange={() => {
                      const recaptchaValue = recaptchaRef.current?.getValue();
                      formik.setFieldValue(RECAPTCHA_KEY, recaptchaValue);
                    }}
                  />
                  <ErrorMesseageTranslation name="recaptcha" />
                </div>
                <div className="form-group tw-text-center">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={formik.isSubmitting}
                  >
                    {translation('submit')}
                  </button>
                  {error && <div className="invalid-feedback tw-block">{error}</div>}
                </div>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CorporateInquiry;
